<template>
  <ValidationObserver ref="observerRef" v-slot="{ handleSubmit }">
    <loading :active.sync="isLoading" :is-full-page="false" />

    <form @submit.prevent="handleSubmit(submitStep)" class="block px-6">
      <div class="sticky top-0 left-0 z-10 bg-white">
        <div class="flex flex-wrap w-full">
          <text-input
            rules="required"
            label="Role name"
            v-model="form.role_name"
          />
        </div>
        <fsTableContainer>
          <fsTable>
            <fsTableHead>
              <fsTableHeadItem
                v-for="(item, index) in tableHeaders"
                :key="`fs-table-header-${index}`"
                :item="item"
                type="permission"
                :className="item.className"
              />
            </fsTableHead>
          </fsTable>
        </fsTableContainer>
      </div>
      <div class="grid grid-cols-1 mt-5 ">
        <!-- start: table-container -->
        <fsTableContainer>
          <loading :active.sync="isLoading" :is-full-page="false" />

          <fsTable>
            <fsTableRow
              v-for="(item, index) in permissions"
              :key="`fs-table-row-${index}`"
            >
              <fsTableRowItem type="permission" className="text-left">
                {{ item.name }}
              </fsTableRowItem>
              <fsTableRowItem
                v-for="(actionType, actionTypeIndex) in actionTypes"
                :key="
                  `action-type-row-${item.action_types.includes(
                    actionType
                  )}-${index}-${actionTypeIndex}`
                "
                type="permission"
              >
                <t-toggle
                  v-if="
                    (availableScreenActionsMap[item.name] || []).includes(
                      actionType
                    ) && actionType === 'VIEW'
                  "
                  :checked="
                    item.action_types.includes(actionType) ? true : false
                  "
                  @change="onChangeToggle(index, actionType)"
                  variant="success"
                />
                <t-toggle
                  v-else-if="
                    (availableScreenActionsMap[item.name] || []).includes(
                      actionType
                    ) && actionType !== 'VIEW'
                  "
                  :checked="
                    item.action_types.includes(actionType) ? true : false
                  "
                  :disabled="
                    permissions[index].action_types.includes('VIEW')
                      ? false
                      : true
                  "
                  @change="onChangeToggle(index, actionType)"
                  variant="success"
                />
                <t-toggle
                  v-else
                  :checked="
                    item.action_types.includes(actionType) ? true : false
                  "
                  :disabled="
                    !(availableScreenActionsMap[item.name] || []).includes(
                      actionType
                    )
                  "
                  variant="success"
                />
              </fsTableRowItem>
            </fsTableRow>
          </fsTable>
        </fsTableContainer>
        <!-- end: table-container -->
      </div>

      <button type="submit" ref="submitButton" class="hidden">
        Save
      </button>
    </form>
  </ValidationObserver>
</template>

<script>
import { useEndpoints } from '@/composables'
import fsTableUiMixin from '@/mixins/fsTableUiMixin'
import TextInput from '@/components/form/TextInput'
import { xMan } from '@/utils'

export default {
  name: 'RoleAddEditStep1',
  mixins: [fsTableUiMixin],
  components: { TextInput },

  props: {
    primaryKey: {
      type: null,
      required: false,
    },
    formData: {
      required: false,
    },
    isEditing: {
      type: Boolean,
      required: false,
      default: false,
    },
    availableScreenActionsMap: {
      type: Object,
      required: true,
    },
    availablePermissions: {
      type: Array,
      required: true,
      default: () => [],
    },
  },

  data() {
    return {
      isLoading: false,
      form: { role_name: null },
      tableHeaders: [
        {
          name: 'Permission',
          width: '32%',
          field: 'id',
          sort: null,
          order: '',
          className: 'text-left',
        },
        {
          name: 'View',
          width: '17%',
          field: 'VIEW',
          sort: null,
          order: '',
        },
        {
          name: 'Create',
          width: '17%',
          field: 'CREATE',
          sort: null,
          order: '',
        },
        {
          name: 'Update',
          width: '17%',
          field: 'UPDATE',
          sort: null,
          order: '',
        },
        {
          name: 'Delete',
          width: '17%',
          field: 'DELETE',
          sort: null,
          order: '',
        },
      ],
      actionTypes: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'],
      permissions: [],
    }
  },
  beforeDestroy() {
    this.resetData()
  },

  watch: {
    formData: {
      deep: true,
      immediate: true,
      handler(data) {
        setTimeout(() => {
          this.resetData()
          if (data) {
            this.form = { role_name: data.role_name }
            data.screenPermissions.forEach((item) => {
              this.permissions.forEach((element) => {
                if (item.id == element.id) {
                  element.action_types = item.action_types
                }
              })
            })
          }
        }, 500)
      },
    },
    availablePermissions: {
      deep: true,
      immediate: true,
      handler(data) {
        if (data.length > 0) {
          this.permissions = data
        }
      },
    },
  },

  methods: {
    onChangeToggle(index, actionType) {
      if (this.permissions[index].action_types.includes(actionType)) {
        if (actionType === 'VIEW') {
          this.permissions[index].action_types = []
        } else {
          this.permissions[index].action_types = this.permissions[
            index
          ].action_types.filter((x) => x !== actionType)
        }
      } else {
        this.permissions[index].action_types.push(actionType)
      }
    },
    async submit() {
      this.$refs.submitButton.click()
    },

    async submitStep() {
      const url = this.isEditing
        ? useEndpoints.role.update(this.primaryKey)
        : useEndpoints.role.create()

      const method = this.isEditing ? 'PATCH' : 'POST'

      const formData = new xMan().toFormData()

      formData.append('role_name', this.form.role_name)
      this.permissions.forEach((element) => {
        if (element.action_types && element.action_types.length) {
          formData.append(
            'permissions',
            `${element.id}:${element.action_types.toString()}`
          )
        }
      })
      if (this.formData) {
        this.formData.hardwarePermissions.forEach((element) => {
          if (element.action_types && element.action_types.length) {
            formData.append(
              'permissions',
              `${element.id}:${element.action_types.toString()}`
            )
          }
        })
      }

      this.isLoading = true
      await this.$http({ method, url, data: formData })
        .then((res) => {
          console.log({ save: res.data })
          this.$emit('save', { step: 1, data: res.data })

          const message = `Role and screen Permission ${
            this.isEditing ? 'updated' : 'added successfully'
          }`

          this.$notify({
            group: 'bottomLeft',
            type: 'success',
            title: 'Success',
            text: message,
          })
        })
        .catch((err) => {
          // todo: emit fail?
          console.error('Error occured!', err.response)
          this.$notify({
            group: 'bottomLeft',
            type: 'error',
            title: 'Error occured!',
            text: err.response.data.message ?? err.response.data,
          })
        })
        .finally(() => {
          this.isLoading = false
          this.resetData()
        })
    },
    resetData() {
      this.permissions.forEach((element) => {
        element.action_types = []
      })
    },
  },
}
</script>

<style lang="scss" scoped></style>
